import { useEffect, useMemo, useState } from 'react';

import ShiftCountLabel from '../../shared/ShiftCountLabel';

// V1 parity: legacy CreateGigOrder's `ShiftWorkersModal`
// (StaffingStep/Common) opens when the admin clicks the worker summary
// area on a shift card. It lists the Hired and Invited workers *for that
// specific shift* and lets the admin cancel a hire / cancel an invite
// (per-row or bulk) scoped to that shift only. V2 previously rendered the
// shift card counts/avatars as static, with no drill-in — so the admin
// could not inspect or cancel a shift's staffing from the card. This
// component restores that workflow. It follows the v2 ExitWizardModal
// overlay pattern (true centered backdrop, inline styles, Manrope) so it
// matches the rest of the v2 proto surface and grabs attention the way
// the legacy shared <Modal> overlay did.

const FONT = "'Manrope', sans-serif";
const BACKDROP_COLOR = 'rgba(15, 23, 32, 0.55)';
const THEME = '#44A735';

export type ShiftWorkerRow = {
  id: number;
  name: string;
  initial: string;
  avatarUrl?: string | null;
};

type ListType = 'hired' | 'invited';

type ShiftWorkersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  // Pre-formatted header strings built by the caller from the shift.
  dateLabel: string;
  timeLabel: string;
  hired: ShiftWorkerRow[];
  invited: ShiftWorkerRow[];
  quantity?: number;
  isClientAdmin: boolean;
  isLta: boolean;
  // Cancels the given workers from this shift only (parent maps to the
  // per-shift worker_shift_scope removal — never a flat job-level wipe).
  onCancelWorkers: (list: ListType, workerIds: number[]) => void;
};

const Avatar = ({ row }: { row: ShiftWorkerRow }) => {
  const [errored, setErrored] = useState(false);
  const showImg = !!row.avatarUrl && !errored;
  return (
    <span
      aria-hidden
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: '#E7F3E6',
        color: THEME,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {showImg ? (
        <img
          src={row.avatarUrl as string}
          alt=""
          onError={() => setErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        row.initial
      )}
    </span>
  );
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: 'none',
  background: '#EBEBEB',
  color: '#262626',
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ShiftWorkersModal = ({
  isOpen,
  onClose,
  dateLabel,
  timeLabel,
  hired,
  invited,
  quantity,
  isClientAdmin,
  isLta,
  onCancelWorkers,
}: ShiftWorkersModalProps) => {
  // Which lists this admin/order can see, mirroring v1's gating:
  //  - tenant admin + GIG  -> Hired & Invited tabs
  //  - tenant admin + LTA  -> Hired only
  //  - client admin + GIG  -> Invited only (starts on Invited)
  //  - client admin + LTA  -> Hired only
  const availableLists: ListType[] = useMemo(() => {
    if (isClientAdmin) return [isLta ? 'hired' : 'invited'];
    if (isLta) return ['hired'];
    return ['hired', 'invited'];
  }, [isClientAdmin, isLta]);

  const [activeList, setActiveList] = useState<ListType>(availableLists[0]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // If role/order context shifts the available lists out from under the
  // active tab, snap back to the first valid list.
  useEffect(() => {
    if (!availableLists.includes(activeList)) {
      setActiveList(availableLists[0]);
      setSelected(new Set());
    }
  }, [availableLists, activeList]);

  // Match v1: the modal auto-dismisses once the shift has no hired and
  // no invited workers left (the last cancel empties it).
  useEffect(() => {
    if (isOpen && hired.length === 0 && invited.length === 0) onClose();
  }, [isOpen, hired.length, invited.length, onClose]);

  // Escape closes (v1 shared <Modal> parity).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // V1 parity: `fullyStaffed = hiredCount > 0 ? hiredCount === quantity
  // : false` (StaffingShiftsCardsSection). Drives the green
  // "Fully Staffed" badge in the CountSection subtitle.
  const fullyStaffed =
    hired.length > 0 &&
    typeof quantity === 'number' &&
    hired.length === quantity;

  const rows = activeList === 'hired' ? hired : invited;
  const cancelLabel = activeList === 'hired' ? 'Cancel Hire' : 'Cancel Invite';
  const showTabs = !isClientAdmin && !isLta;
  const heading = activeList === 'hired' ? 'Hired Workers' : 'Invited Workers';

  const switchList = (next: ListType) => {
    setActiveList(next);
    setSelected(new Set());
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };

  const cancelOne = (id: number) => onCancelWorkers(activeList, [id]);
  const cancelSelected = () => {
    if (selected.size === 0) return;
    onCancelWorkers(activeList, Array.from(selected));
    setSelected(new Set());
  };

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: BACKDROP_COLOR,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Shift workers"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18)',
          width: '100%',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: FONT,
          color: '#1C1C1C',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '20px 24px 12px',
          }}
        >
          <div>
            <h4
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: 0,
                fontFamily: FONT,
              }}
            >
              {dateLabel}
            </h4>
            <p
              style={{
                fontSize: 13,
                color: '#7A7A7A',
                margin: '4px 0 0 0',
                fontFamily: FONT,
              }}
            >
              {timeLabel}
              {' · '}
              <ShiftCountLabel
                isLta={isLta}
                isClientAdmin={isClientAdmin}
                hiredCount={hired.length}
                invitedCount={invited.length}
                quantity={quantity}
                fullyStaffed={fullyStaffed}
              />
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 22,
              lineHeight: 1,
              cursor: 'pointer',
              color: '#7A7A7A',
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {showTabs ? (
          <div
            style={{
              display: 'flex',
              gap: 24,
              padding: '0 24px',
              borderBottom: '1px solid #EDEDED',
            }}
          >
            {(['hired', 'invited'] as ListType[]).map((list) => {
              const active = activeList === list;
              const count = list === 'hired' ? hired.length : invited.length;
              return (
                <button
                  key={list}
                  type="button"
                  onClick={() => switchList(list)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: '10px 0',
                    cursor: 'pointer',
                    fontFamily: FONT,
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    color: active ? THEME : '#7A7A7A',
                    borderBottom: active
                      ? `2px solid ${THEME}`
                      : '2px solid transparent',
                  }}
                >
                  {list === 'hired' ? 'Hired' : 'Invited'} Workers ({count})
                </button>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              padding: '0 24px 10px',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {heading}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px 0',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: THEME,
              cursor: rows.length > 0 ? 'pointer' : 'default',
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              disabled={rows.length === 0}
              onChange={toggleSelectAll}
            />
            Select all
          </label>
          {selected.size > 0 ? (
            <button
              type="button"
              onClick={cancelSelected}
              style={cancelBtnStyle}
            >
              − {cancelLabel} ({selected.size})
            </button>
          ) : null}
        </div>

        <div
          style={{
            overflowY: 'auto',
            padding: '8px 24px 24px',
            flex: 1,
          }}
        >
          {rows.length === 0 ? (
            <p
              style={{
                color: '#7A7A7A',
                fontSize: 14,
                padding: '24px 0',
                textAlign: 'center',
                margin: 0,
              }}
            >
              No {activeList} workers for this shift.
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid #F2F2F2',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(row.id)}
                  onChange={() => toggleRow(row.id)}
                  aria-label={`Select ${row.name}`}
                />
                <Avatar row={row} />
                <span
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.name}
                >
                  {row.name}
                </span>
                <button
                  type="button"
                  aria-label={`${cancelLabel} for ${row.name}`}
                  disabled={selected.has(row.id)}
                  onClick={() => cancelOne(row.id)}
                  style={{
                    ...cancelBtnStyle,
                    color: selected.has(row.id) ? '#7A7A7A' : '#262626',
                    cursor: selected.has(row.id) ? 'default' : 'pointer',
                  }}
                >
                  − {cancelLabel}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftWorkersModal;
